import { t } from "@hakili/i18n";
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

/**
 * `G-D4` (`P1-DLW-055`), R's batches 4 and 7 (`design/dealer-v2-r` `98e70c63`): **the car page's tab strip, local to the dealer** (no shared kit change).
 *
 * The strip holds the tabs that have something to show: Vente, Dossiers, Expertise, Photos, Papiers, Journal. **« Offres » and « Prospects » are not tabs yet**: a tab whose whole content is a promise is an unbuilt control (R's review,
 * the architect's ruling of 2 November, A49). Their URL values stay RESERVED: `?onglet=offres` and `?onglet=prospects` open « Vente » until lane B builds the offers board and adds the tab (its test ids, `vehicule-onglet-offres` and
 * `vehicule-panneau-offres`, are the strip's own naming and are reserved with it). The page opens on « Vente » (the frames: « la page s'ouvre sur où en est la vente »).
 *
 * **The tab lives in the URL**, `?onglet=<tab>` on `/vehicules/:id`, so that lane B's offers board and N4's in-app notices can link to a car's tab. An unknown or reserved value opens « Vente ». A tab change is a navigation (the back
 * button follows it).
 *
 * **Every panel stays mounted** and is hidden while another tab is open, so a read in flight or a field half typed survives a tab change, and no section is read twice.
 */
export const CAR_TABS = ["vente", "dossiers", "expertise", "photos", "papiers", "journal"] as const;
export type CarTab = (typeof CAR_TABS)[number];

export const DEFAULT_CAR_TAB: CarTab = "vente";
/** The query parameter lane B and the notices link with. */
export const CAR_TAB_PARAM = "onglet";

const LABELS: Readonly<Record<CarTab, string>> = {
  vente: "dealer.vehicules.ongletVente",
  dossiers: "dealer.vehicules.ongletDossiers",
  expertise: "dealer.vehicules.ongletExpertise",
  photos: "dealer.vehicules.ongletPhotos",
  papiers: "dealer.vehicules.ongletPapiers",
  journal: "dealer.vehicules.ongletJournal",
};

/** The tab a query string asks for, or « Vente » when it asks for nothing known (a reserved value, `offres` or `prospects`, included). */
export const parseCarTab = (search: string): CarTab => {
  const asked = new URLSearchParams(search).get(CAR_TAB_PARAM);
  return CAR_TABS.find((tab) => tab === asked) ?? DEFAULT_CAR_TAB;
};

/** The URL of a car's tab: the same path, the other tabs' query kept, `onglet` set. */
export const carTabUrl = (pathname: string, search: string, tab: CarTab): string => {
  const params = new URLSearchParams(search);
  params.set(CAR_TAB_PARAM, tab);
  return `${pathname}?${params.toString()}`;
};

/** The open tab, from the URL, and the way to change it (a navigation). */
export function useCarTab(naviguer?: (to: string) => void): readonly [CarTab, (tab: CarTab) => void] {
  const [search, setSearch] = useState(() => globalThis.location.search);
  useEffect(() => {
    const onPop = (): void => {
      setSearch(globalThis.location.search);
    };
    globalThis.addEventListener("popstate", onPop);
    return () => {
      globalThis.removeEventListener("popstate", onPop);
    };
  }, []);
  const select = useCallback(
    (tab: CarTab): void => {
      const to = carTabUrl(globalThis.location.pathname, globalThis.location.search, tab);
      if (naviguer === undefined) globalThis.history.pushState(null, "", to);
      else naviguer(to);
      setSearch(globalThis.location.search);
    },
    [naviguer],
  );
  return [parseCarTab(search), select];
}

export function CarTabStrip({
  tab,
  onSelect,
}: {
  readonly tab: CarTab;
  readonly onSelect: (tab: CarTab) => void;
}): React.JSX.Element {
  const tabs = CAR_TABS;
  const refs = useRef(new Map<CarTab, HTMLButtonElement>());
  const move = (event: KeyboardEvent, from: CarTab): void => {
    const at = tabs.indexOf(from);
    const target =
      event.key === "ArrowRight"
        ? tabs[(at + 1) % tabs.length]
        : event.key === "ArrowLeft"
          ? tabs[(at - 1 + tabs.length) % tabs.length]
          : event.key === "Home"
            ? tabs[0]
            : event.key === "End"
              ? tabs[tabs.length - 1]
              : undefined;
    if (target === undefined) return;
    event.preventDefault();
    onSelect(target);
    refs.current.get(target)?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label={t("dealer.vehicules.ongletsLabel")}
      className="mt-5 flex flex-wrap gap-1 border-b border-gray-200"
      data-testid="vehicule-onglets"
    >
      {tabs.map((one) => (
        <button
          key={one}
          ref={(node) => {
            if (node === null) refs.current.delete(one);
            else refs.current.set(one, node);
          }}
          type="button"
          role="tab"
          id={`vehicule-onglet-${one}`}
          aria-selected={one === tab}
          aria-controls={`vehicule-panneau-${one}`}
          tabIndex={one === tab ? 0 : -1}
          onClick={() => {
            onSelect(one);
          }}
          onKeyDown={(event) => {
            move(event, one);
          }}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
            one === tab
              ? "border-indigo-600 text-indigo-700"
              : "border-transparent text-[color:var(--hk-text-2)] hover:text-gray-900"
          }`}
          data-testid={`vehicule-onglet-${one}`}
        >
          {t(LABELS[one])}
        </button>
      ))}
    </div>
  );
}

/** One tab's panel: mounted always, hidden while another tab is open. */
export function CarTabPanel({
  tab,
  active,
  children,
}: {
  readonly tab: CarTab;
  readonly active: CarTab;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <div
      role="tabpanel"
      id={`vehicule-panneau-${tab}`}
      aria-labelledby={`vehicule-onglet-${tab}`}
      hidden={tab !== active}
      className="mt-4"
      data-testid={`vehicule-panneau-${tab}`}
    >
      {children}
    </div>
  );
}
